import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  IStudioWizardApiModel,
  IStudioWizardFormModel,
  studioWizardFormDefaultValues,
  studioWizardFormSchema,
} from "../models/studioWizardFormModel";
import { useEffect } from "react";
import { useFormValidationMessages } from "../../../../../common/infrastructure/hooks/useFormHelper";
import { ISelectFieldModel } from "../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { splitNotificationEmails } from "../models/reservationNotificationEmails";
import { CommissionsVisibility } from "../../../../../common/enums";
import {
  designStudioColorKeys,
  pickDesignStudioColors,
} from "../designStudio/utils/designStudioPaletteUtility";

export const useStudioWizardForm = (
  id?: string,
  tenantOptions?: ISelectFieldModel[],
  studio?: IStudioWizardApiModel,
  defaultTimezone?: string,
) => {
  const form = useForm<IStudioWizardFormModel>({
    defaultValues: studioWizardFormDefaultValues,
    resolver: yupResolver(studioWizardFormSchema),
  });

  // No input on the page holds Design Studio's colours any more (the dialog writes them
  // with setValue), so they are registered by hand: resetField skips fields nobody
  // registered. Declared before the effect that loads the studio, so it runs first.
  useEffect(() => {
    designStudioColorKeys.forEach((key) => form.register(key));
  }, [form]);

  useEffect(() => {
    if (
      form.watch("ownerId") === 0 &&
      tenantOptions &&
      tenantOptions?.length > 0
    ) {
      form.setValue("ownerId", tenantOptions![0].id);
    }

    if (id) {
      form.setValue("id", Number(id));
    }
    if (studio) {
      form.setValue("name", studio.name);
      form.setValue("email", studio.email);
      form.setValue("address", studio.address);
      // Null from the API means "no exact link" — the text input has no null, so the form
      // models that as an empty string and the backend turns it back into null on save.
      form.setValue("addressMapUrl", studio.addressMapUrl ?? "");
      form.setValue("phoneNumber", studio.phoneNumber);
      form.setValue("phoneNumberDescription", studio.phoneNumberDescription);
      form.setValue("secondaryPhoneNumber", studio.secondaryPhoneNumber);
      form.setValue(
        "secondaryPhoneNumberDescription",
        studio.secondaryPhoneNumberDescription,
      );
      form.setValue("ownerId", studio.ownerId);
      // Design Studio's seven colours load as the fields' default values, not as edits:
      // "Незапазени промени" compares them with the saved studio, so re-applying the
      // saved palette is no change and going back to the Planny defaults is one. Null
      // from the API ("no accent") is an empty string in the form.
      const savedColors = pickDesignStudioColors(studio);
      designStudioColorKeys.forEach((key) =>
        form.resetField(key, { defaultValue: savedColors[key] }),
      );
      // Null from the API means "no custom color chosen" — the form models that as an empty
      // string, since the color picker has no null.
      form.setValue("linkPageButtonColor", studio.linkPageButtonColor ?? "");
      form.setValue("linkPageShowPhone", studio.linkPageShowPhone);
      form.setValue("linkPageShowAddress", studio.linkPageShowAddress);
      form.setValue("url", studio.url);
      form.setValue("types", studio.types ?? []);
      form.setValue("timeStep", studio.timeStep);
      form.setValue("isTraining", studio.isTraining);
      form.setValue(
        "showCalendarTileInPortal",
        studio.showCalendarTileInPortal,
      );
      form.setValue(
        "limitUserToOneReservationPerDay",
        studio.limitUserToOneReservationPerDay,
      );
      form.setValue(
        "studioUsesDifferentPlanTypes",
        studio.studioUsesDifferentPlanTypes,
      );
      form.setValue(
        "usesPreReservationForms",
        studio.usesPreReservationForms,
      );
      // Studios saved before the toggles existed come back without them — they keep
      // showing plans and workouts, not the new-studio default of hiding them.
      form.setValue(
        "studioDoesNotUsePlans",
        studio.studioDoesNotUsePlans ?? false,
      );
      form.setValue(
        "studioDoesNotUseWorkouts",
        studio.studioDoesNotUseWorkouts ?? false,
      );
      form.setValue(
        "studioDoesNotUseBodyMetrics",
        studio.studioDoesNotUseBodyMetrics ?? false,
      );
      form.setValue(
        "studioDoesNotUseGoals",
        studio.studioDoesNotUseGoals ?? false,
      );
      form.setValue(
        "studioDoesNotUseServicePortfolios",
        studio.studioDoesNotUseServicePortfolios ?? false,
      );
      form.setValue("useGroupClassCategories", studio.useGroupClassCategories);
      form.setValue(
        "usersCanCancelReservations",
        studio.usersCanCancelReservations,
      );
      form.setValue(
        "displayClosedReservationsLabel",
        studio.displayClosedReservationsLabel,
      );
      form.setValue(
        "askForUserBirthdayOnRegistration",
        studio.askForUserBirthdayOnRegistration,
      );
      form.setValue("disableGoogleLogin", studio.disableGoogleLogin);
      form.setValue(
        "allowLateCancellationsWithPayment",
        studio.allowLateCancellationsWithPayment,
      );
      form.setValue(
        "autoMarkUserAsUnreliableOnLateCancellation",
        studio.autoMarkUserAsUnreliableOnLateCancellation,
      );

      // Studios saved before the setting existed come back without it — they keep the
      // behaviour they had, which is showing the commissions in both reports.
      form.setValue(
        "commissionsVisibleIn",
        studio.commissionsVisibleIn ?? CommissionsVisibility.Both,
      );

      form.setValue("usesHalls", studio.usesHalls);
      form.setValue(
        "showAvailableSeatsInPortal",
        studio.showAvailableSeatsInPortal,
      );
      form.setValue("eventBasePrice", studio.eventBasePrice);
      form.setValue("eventBaseFreeSpaces", studio.eventBaseFreeSpaces);
      form.setValue(
        "minutesBeforeEventCancellationCloses",
        studio.minutesBeforeEventCancellationCloses,
      );
      form.setValue(
        "minutesAfterEventToMarkAsMissed",
        studio.minutesAfterEventToMarkAsMissed,
      );
      form.setValue(
        "reservationCloseTimeBeforeEventMinutes",
        studio.reservationCloseTimeBeforeEventMinutes,
      );
      form.setValue(
        "scheduleVisibilityDaysAhead",
        studio.scheduleVisibilityDaysAhead,
      );
      form.setValue(
        "scheduleVisibilityDaysAheadForSubscribedUsers",
        studio.scheduleVisibilityDaysAheadForSubscribedUsers,
      );
      form.setValue("timezone", studio.timezone);
      form.setValue(
        "percentLeftAfterMultisportTaxes",
        studio.percentLeftAfterMultisportTaxes,
      );
      form.setValue(
        "percentLeftAfterCoolFitTaxes",
        studio.percentLeftAfterCoolFitTaxes,
      );
      form.setValue("supportedTypesOfPayment", studio.supportedTypesOfPayment);
      form.setValue(
        "firstReminderHoursBefore",
        studio.firstReminderHoursBefore,
      );
      form.setValue(
        "secondReminderHoursBefore",
        studio.secondReminderHoursBefore,
      );
      // The API returns the recipients as one comma-separated string; the form
      // works with one row per address.
      form.setValue(
        "reservationNotificationEmails",
        splitNotificationEmails(studio.reservationNotificationEmail),
      );
      // Null means "not set" on the API; the input needs a string to stay
      // controlled, and an empty one maps back to null on save.
      form.setValue("emailSenderName", studio.emailSenderName ?? "");
      form.setValue("smsReminderHoursBefore", studio.smsReminderHoursBefore);
      form.setValue("smsReminderContent", studio.smsReminderContent);
      // Studios saved before the device reminder existed come back without it.
      form.setValue(
        "pushReminderHoursBefore",
        studio.pushReminderHoursBefore ?? null,
      );
    }

    if (defaultTimezone) {
      form.setValue("timezone", defaultTimezone);
    }
  }, [tenantOptions, studio, id, defaultTimezone]);

  useFormValidationMessages<IStudioWizardFormModel>(form);

  return form;
};
